import { HttpClient, HttpContext } from "@angular/common/http";
import { computed, effect, inject, Injectable, Signal, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { DeviceService, PropertyService } from "@core/services";
import { environment } from "@environments/environment";
import { CUSTOM_ERROR_MESSAGE, Room } from "@shared/models";
import { AddRoomRequest } from "@shared/models/add-room-request.model";
import { RoomType } from "@shared/models/rooms/room-type.model";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
/**
 * Service for managing room data and operations.
 * 
 * Provides comprehensive functionality to fetch, cache, and manage rooms associated with properties.
 * Handles room data synchronization with devices and supports real-time updates through Angular signals
 * and reactive programming patterns.
 * 
 * @example
 * ```typescript
 * constructor(private roomService: RoomService) {}
 * 
 * ngOnInit() {
 *   // Get all rooms for the currently selected property
 *   const rooms = this.roomService.rooms();
 *   
 *   // Get a specific room with its associated devices
 *   const selectedRoom = this.roomService.getSelectedRoom(roomId);
 *   
 *   // Fetch a specific room and update the cache
 *   this.roomService.getRoomById(roomId, propertyId).subscribe(room => {
 *     console.log('Room loaded:', room);
 *   });
 * }
 * ```
 * 
 * @remarks
 * - Uses Angular signals for reactive state management
 * - Implements resource-based caching with force reload capability
 * - Automatically reloads rooms when selected property changes via effects
 * - Integrates with DeviceService to automatically associate devices with rooms
 * - Includes error handling and loading state management
 * - All public properties are read-only computed/signal values
 * 
 * @class RoomService
 */
export class RoomService {
  private readonly deviceService = inject(DeviceService);
  private readonly propertyService = inject(PropertyService);

  private forceReload = signal<boolean>(false);
  private roomReloadingSignal = signal<boolean>(false);
  private roomTypesSignal = signal<RoomType[]>([]);

  /**
   * Computed signal containing all rooms for the currently selected property.
   * Automatically updates when the property selection changes.
   * 
   * @readonly
   * @type {Signal<Room[]>}
   */
  readonly rooms = computed(() => this.roomsResource.value() ?? []);

  /**
   * Read-only signal indicating whether a room is currently being loaded.
   * Used to display loading indicators in the UI during individual room fetch operations.
   * 
   * @readonly
   * @type {Signal<boolean>}
   */
  readonly roomReloading = this.roomReloadingSignal.asReadonly();

  readonly roomTypes = this.roomTypesSignal.asReadonly();

  constructor(private http: HttpClient) {
    effect(() => {
      const property = this.propertyService.selectedProperty();
      if (property) {
        this.roomsResource.reload();
      }
    });
  }

  /**
   * RxJS resource for managing room data caching and loading.
   * Automatically triggers reload when the selected property changes.
   * Caches rooms to avoid unnecessary API calls unless force reload is triggered.
   * 
   * @remarks
   * - Returns an empty array if no property is selected
   * - Includes built-in error handling and caching logic
   * - Stream is automatically triggered when dependent signals change
   */
  roomsResource = rxResource({
    params: () => {
      const property = this.propertyService.selectedProperty();
      return { id: property?.placeId ?? null, force: this.forceReload() };
    },
    stream: (params) => {
      if (!params.params.id) {
        return of([] as Room[]);
      }

      return this.getRoomsByProperty(params.params.id, params.params.force);
    },
    defaultValue: [] as Room[]
  });

  /**
   * Fetches rooms for a specific property from the API.
   * Implements caching logic to avoid redundant API calls.
   * 
   * @param {number} propertyId - The ID of the property to fetch rooms for
   * @param {boolean} [force=false] - If true, bypasses cache and forces a fresh API call
   * @returns {Observable<Room[]>} Observable emitting the array of rooms for the property
   * 
   * @remarks
   * - Caches results and returns cached data if the same property is requested
   * - Maps raw API responses to Room model instances
   * - Returns empty array from cache if force flag is false and cache is populated
   * 
   * @example
   * ```typescript
   * this.roomService.getRoomsByProperty(123).subscribe(rooms => {
   *   console.log('Loaded rooms:', rooms);
   * });
   * 
   * // Force refresh from API
   * this.roomService.getRoomsByProperty(123, true).subscribe(rooms => {
   *   console.log('Fresh rooms:', rooms);
   * });
   * ```
   */
  getRoomsByProperty(propertyId: number, force = false): Observable<Room[]> {
    const currentRooms = this.roomsResource.value() ?? [];
    if (!force && currentRooms.length > 0 && currentRooms[0].placeId === propertyId) {
      return of(currentRooms);
    }

    let httpContext = new HttpContext().set(CUSTOM_ERROR_MESSAGE, "Failed to load rooms. Please try again.");

    return this.http
      .get<Room[]>(`${environment.apiUrl}/v1/places/${propertyId}/rooms`, { context: httpContext })
      .pipe(
        map((rooms) => rooms.map((room) => new Room(room)))
      );
  }

  /**
   * Retrieves a specific room by ID with its associated devices.
   * Returns a computed signal that automatically updates when the room or device data changes.
   * 
   * @param {Signal<string>} roomId - A signal emitting the room ID to select
   * @returns {Signal<Room | null>} A computed signal emitting the selected room with devices or null
   * 
   * @remarks
   * - The returned signal is reactive and updates automatically when the input signal changes
   * - Automatically associates all devices belonging to the room via the DeviceService
   * - Returns null if the room is not found in the cache
   * - Does not trigger API calls; uses cached data only
   * 
   * @example
   * ```typescript
   * const roomId = signal<string>('room-123');
   * const selectedRoom = this.roomService.getSelectedRoom(roomId);
   * 
   * // In template
   * {{ selectedRoom()?.name }}
   * {{ selectedRoom()?.devices?.length }}
   * ```
   */
  getSelectedRoom(roomId: Signal<string>): Signal<Room | null> {
    return computed(() => {
      const allRooms = this.rooms();
      const allDevices = this.deviceService.devices();

      const room = allRooms.find((r) => r.refId === roomId());

      if (!room) return null;

      room.devices = allDevices.filter((d) => d.fkRoom === room.refId);
      return room;
    });
  }

  /**
   * Fetches a specific room by ID from the API and updates the cache.
   * Associates the room with its devices and updates the loading state.
   * 
   * @param {string} roomId - The ID of the room to fetch
   * @param {number} propertyId - The ID of the property containing the room
   * @returns {Observable<Room>} Observable emitting the fetched room with devices
   * 
   * @remarks
   * - Sets the roomReloading signal to true during the request
   * - Automatically updates the rooms cache (updates existing or adds new)
   * - Associates devices from DeviceService with the fetched room
   * - Handles errors gracefully and resets the loading state
   * - The roomReloading signal is reset to false on completion or error
   * 
   * @example
   * ```typescript
   * this.roomService.getRoomById('room-123', 1).subscribe({
   *   next: (room) => console.log('Room loaded:', room),
   *   error: (err) => console.error('Failed to load room:', err)
   * });
   * ```
   */
  getRoomById(roomId: string, propertyId: number): Observable<Room> {
    this.roomReloadingSignal.set(true);

    return this.http
      .get<Room>(
        `${environment.apiUrl}/v1/places/${propertyId}/rooms/${roomId}`,
      )
      .pipe(
        map((response) => {
          let room = new Room(response);
          room.devices = this.deviceService
            .devices()
            .filter((d) => d.fkRoom === room.refId);
          return room;
        }),
        tap((room) => {
          this.roomsResource.update((rooms) => {
            const existingIndex = rooms.findIndex((r) => r.refId === room.refId);
            if (existingIndex !== -1) {
              rooms[existingIndex] = room;
            } else {
              rooms.push(room);
            }
            return rooms;
          });
          this.roomReloadingSignal.set(false);
        }),
        catchError((error) => {
          this.roomReloadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Clears all cached rooms from the service.
   * Useful for cleanup when switching properties or during logout.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Resets the rooms cache to an empty array
   * - Does not affect the DeviceService cache
   * - The rooms signal will re-compute to an empty array
   * 
   * @example
   * ```typescript
   * // Clear rooms when logging out
   * logout() {
   *   this.roomService.clearRooms();
   *   // ... other cleanup
   * }
   * ```
   */
  clearRooms(): void {
    this.roomsResource.set([]);
  }

  /**
   * Forces a refresh of the rooms cache from the API.
   * Bypasses the normal caching mechanism and fetches fresh data.
   * 
   * @returns {void}
   * 
   * @remarks
   * - Uses queueMicrotask to ensure the signal toggle happens in the microtask queue
   * - Triggers a reload of the roomsResource with force=true
   * - Useful for manual refresh functionality in the UI
   * - Should be called sparingly to avoid excessive API calls
   * 
   * @example
   * ```typescript
   * // Provide a refresh button in the UI
   * onRefreshClick() {
   *   this.roomService.forceRefresh();
   * }
   * ```
   */
  forceRefresh(): void {
    this.forceReload.set(true);
    queueMicrotask(() => this.forceReload.set(false));
  }

  addRoom(newRoom: AddRoomRequest, propertyId: number): Observable<Room> {
    return this.http.post<Room>(`${environment.apiUrl}/v1/places/${propertyId}/rooms`, newRoom).pipe(
      map((response) => new Room(response)),
      tap((room) => {
        this.roomsResource.update((rooms) => [...rooms, room]);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getRoomTypes(propertyId: number): Observable<RoomType[]> {
    if (this.roomTypes().length > 0 && this.roomTypes()[0].placeId === propertyId) {
      return of(this.roomTypes());
    }

    return this.http.get<RoomType[]>(`${environment.apiUrl}/v1/places/${propertyId}/roomTypes`).pipe(
      map((response) => response.map((dto) => new RoomType(dto))),
      tap((roomTypes) => this.roomTypesSignal.set(roomTypes)),
      catchError((error) => {
        return of([]);
      })
    );
  }
}
